import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SeConstraintCheckerService } from '../se-constraint-checker.service/se-constraint-checker.service';
import { SeConstraintConfiguratorService } from '../se-constraint-configurator.service/se-constraint-configurator.service';
import { IConstraint, IConstraintMatchInfo, IConstraintPartialResult } from '../models/model.constraint';
import { SeConstraintValidatorService } from '../se-constraint-validator.service/se-constraint-validator.service';
import { ConstraintTypeEnum } from '../models/enum';
import { MatchingResult } from '../models/abstractions';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { find, flatMap, isEqual, isUndefined, uniq } from 'lodash-es';
import { Dict } from '../../utils/dictionary';
import { DataBinding } from '../../utils/data-binding';

@Injectable({
    providedIn: 'root',
})
export class SeConstraintNotifierService {
    private readonly constraintMatchStream = new BehaviorSubject<Dict<Dict<IConstraintMatchInfo>>>({}); //constraintId - pathEntity - match

    constructor(
        private configService: SeConstraintConfiguratorService,
        private validatorService: SeConstraintValidatorService,
        private checkerService: SeConstraintCheckerService,
    ) {
        //una volta completato il refactoring questo non serve più qui
    }

    public notifyConstraintMatch(
        constraint: IConstraint,
        matchingResult: MatchingResult,
        partialResults?: IConstraintPartialResult[] | null,
    ) {
        const dictConstraintMatches: Dict<Dict<IConstraintMatchInfo>> = this.constraintMatchStream.value;
        let dictEntityMatches: Dict<IConstraintMatchInfo> = dictConstraintMatches[constraint.id.toLowerCase()];

        if (!dictEntityMatches) dictEntityMatches = {};

        dictEntityMatches[''] = {
            constraint: constraint,
            matchingResult: matchingResult,
            partialResults: partialResults ?? undefined,
        };

        if (!!partialResults) {
            const constraintsToNotify = partialResults.filter((pr) => !!pr.checkedEntity);
            constraintsToNotify.forEach(
                (pr) =>
                    /** nei test viene inserito un valore undefined come chiave del dictionary non credo sia voluto */
                    (dictEntityMatches[pr.checkedEntity?.path?.toStringSimple()] = {
                        constraint: constraint,
                        matchingResult: pr.matchingResult,
                    }),
            );
        }

        //dictConstraintMatches[constraint.id.toLowerCase()] = { constraint: constraint, matchingResult: matchingResult, partialResults: partialResults }

        dictConstraintMatches[constraint.id.toLowerCase()] = dictEntityMatches;
        this.constraintMatchStream.next(dictConstraintMatches);
    }

    public notifyAllConstraint() {
        //ricalcola tutti i constraint tranne i valori normali che sono fatti sempre on-demand
        this.notifyConstraintMatches(
            this.configService.allConstraints.filter(
                (c) =>
                    c.constraintType != ConstraintTypeEnum.NormalValue &&
                    c.constraintType != ConstraintTypeEnum.CustomValue,
            ),
        );
    }

    public notifyConstraintById(constraintId: string) {
        this.notifyConstraintMatches(
            this.configService.allConstraints.filter((constraint) => constraint.id === constraintId),
        );
    }

    /**
     * Notifica il cambiamento di un valore. L'effetto è il ricalcolo dei constraint applicati
     * al binding passato in input. Il percorso del binding può essere anche parziale
     * (vedi specifica metodo getSourcesInsideASpecificPath)
     */
    public notifyValueChanged(bindingToFix: DataBinding) {
        //per ogni constraint associato:
        //match = guardo se il constraint matcha
        const bindingPath = bindingToFix.toStringForNotification();
        const binding = new DataBinding(bindingPath);

        let currentSources = this.configService.constraintSources.getSourcesInsideASpecificPath(
            binding.toStringSimple(),
        );
        let currentAdvSources = this.configService.constraintSources.getSourcesInsideASpecificPath(binding.toString());

        if (!!currentSources?.length) {
            let constraints: IConstraint[] = uniq(flatMap(currentSources.map((s) => s.constraints)));
            this.notifyConstraintMatches(constraints);
        } else if (!!currentAdvSources?.length) {
            let constraints: IConstraint[] = uniq(flatMap(currentAdvSources.map((s) => s.constraints)));
            this.notifyConstraintMatches(constraints);
        }
    }

    public register(constraintId: string, contextPath: string = ''): Observable<IConstraintMatchInfo> {
        const entityPath = !contextPath ? '' : contextPath;

        return this.constraintMatchStream.asObservable().pipe(
            map((cs) => cs[constraintId.toLowerCase()]),
            filter((dictConstraintMatch: Dict<IConstraintMatchInfo>) =>
                this.containsConstraintMatchOfEntityPath(dictConstraintMatch, entityPath),
            ),
            map((cs) => (isUndefined(cs[entityPath]) ? cs[''] : cs[entityPath])),
            distinctUntilChanged((x, y) => this.areConstraintMatchEquals(x, y)),
        );
    }

    // #region privates

    private areConstraintMatchEquals(x: IConstraintMatchInfo, y: IConstraintMatchInfo): boolean {
        const equalsOnGlobalResult = x.matchingResult === y.matchingResult;
        const observePartialResults = this.isConstraintOnlyOnPartialResults(x.constraint);

        if (!observePartialResults) return equalsOnGlobalResult;

        if (!equalsOnGlobalResult) return false;

        if (!x.partialResults && !y.partialResults) return true;

        if ((!x.partialResults && y.partialResults) || (x.partialResults && !y.partialResults)) return false;

        for (const xPartialRes of x?.partialResults ?? []) {
            const xEntity = xPartialRes.checkedEntity;

            const yPartialRes = find(y.partialResults, (pr) => isEqual(pr.checkedEntity, xEntity));

            if (!yPartialRes)
                //nell'altro risultato parziale non c'è la entity cercata
                return false;

            if (xPartialRes.matchingResult !== yPartialRes.matchingResult) return false;
        }

        //tutti i risultati delle entity di uno sono uguali ai risultati di tutte le entity dell'altro
        return true;
    }

    /**
     * Dato un dizionario key: entityPath - value: constraintMatch ritorna true solo se il dizionario contiene un constraint match per l'entityPath passata in input.
     * Questo significa che una determinata regola è stata verificata per quella entity.
     * L'entityPath può essere anche vuota (che significa "regola verificata per tutte le entity").
     * Se nel dizionario c'è una voce vuota, allora viene tornato true, perchè quella entry vale sicuramente anche per la entityPath in input-
     */
    private containsConstraintMatchOfEntityPath(
        dictConstraintMatch: Dict<IConstraintMatchInfo>,
        entityPath: string,
    ): boolean {
        if (!dictConstraintMatch) return false;

        return !!dictConstraintMatch[entityPath] || !!dictConstraintMatch[''];
    }

    private notifyConstraintMatches(constraints: IConstraint[]) {
        if (!constraints) return;

        //TODO: stabilire un ordine? (ad esempio: le validazioni tutte alla fine...)

        for (let constraint of constraints) {
            //controllo il match e notifico
            let match;
            let partialResults: IConstraintPartialResult[] | null = null;

            if (constraint.constraintType == ConstraintTypeEnum.Validation) {
                match = this.validatorService.validateConstraint(constraint);
            } else {
                const checkOnlyPartialResults = this.isConstraintOnlyOnPartialResults(constraint);
                const constraintResult = this.checkerService.checkConstraint(constraint, checkOnlyPartialResults)[0];
                match = constraintResult?.matchingResult;
                partialResults = constraintResult?.partialResults;
            }

            this.notifyConstraintMatch(constraint, match, partialResults);
        }
    }

    private isConstraintOnlyOnPartialResults(constraint: IConstraint): boolean {
        return constraint.constraintType == ConstraintTypeEnum.Filter;
    }

    // #endregion
}
